import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TeamChatProvider, useTeamChat } from '@/context/TeamChatContext';
import ChatArea from '@/components/inbox/components/team-chat/ChatArea';
import ThreadPanel from '@/components/inbox/components/team-chat/ThreadPanel';
import NewMessageDialog from '@/components/inbox/components/team-chat/NewMessageDialog';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { projectService, type Project } from '@/lib/projectService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import { Lock, ChevronDown, ChevronRight, Plus, MessageSquare } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// ─── Portal-specific sidebar ─────────────────────────────────────────────────

interface PortalChatSidebarProps {
  projects: Project[];
  isGlobalDarkMode: boolean;
  onChatSelect?: () => void;
}

const PortalChatSidebar: React.FC<PortalChatSidebarProps> = ({ projects, isGlobalDarkMode, onChatSelect }) => {
  const {
    channels,
    directMessages,
    activeChannelId,
    activeDmId,
    setActiveChannel,
    setActiveDm,
    getDmParticipant,
    currentUser,
    onlineUsers,
  } = useTeamChat();

  const [groupChatsOpen, setGroupChatsOpen] = useState(true);
  const [dmsOpen, setDmsOpen] = useState(true);
  const [newMessageOpen, setNewMessageOpen] = useState(false);

  // Only channels linked to the client's projects
  const projectChannelIds = new Set(projects.map(p => p.channel_id).filter(Boolean));
  const projectChannels = channels.filter(
    c => projectChannelIds.has(c.id) && currentUser && c.members.includes(currentUser.id)
  );

  const textColor = isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-600';
  const sidebarBg = isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light-secondary';

  return (
    <div className={cn("h-full w-full flex flex-col border-r", sidebarBg, isGlobalDarkMode ? "border-sidebar-border" : "border-gray-200")}>
      {/* Header */}
      <div className={cn("px-6 py-4 lg:py-6 border-b", isGlobalDarkMode ? "border-sidebar-border" : "border-gray-200")}>
        <h3 className={cn("text-lg font-semibold", textColor)}>Inbox</h3>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <nav className="p-3">

          {/* ── Group Chats ── */}
          <Collapsible open={groupChatsOpen} onOpenChange={setGroupChatsOpen} className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <CollapsibleTrigger asChild>
                  <button className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}>
                    {groupChatsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </CollapsibleTrigger>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
                  Group Chats
                </h3>
              </div>
            </div>

            <CollapsibleContent>
              {projectChannels.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-2 text-center">
                  <MessageSquare className="w-7 h-7 text-gray-300 mb-2" />
                  <p className="text-xs text-gray-400">No project channels yet</p>
                </div>
              ) : (
                <ul className="space-y-1">
                  {projectChannels.map(channel => {
                    const isActive = activeChannelId === channel.id;
                    const hasOtherOnline = channel.members.some(
                      m => m !== currentUser?.id && onlineUsers.includes(m)
                    );
                    return (
                      <li key={channel.id}>
                        <button
                          onClick={() => { setActiveChannel(channel.id); onChatSelect?.(); }}
                          className={cn(
                            'w-full px-3 py-3 lg:py-2.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 text-left active:scale-[0.98]',
                            isActive
                              ? (isGlobalDarkMode ? 'bg-nav-active text-white shadow-md font-bold' : 'bg-sidebar-light-active text-gray-900 font-bold shadow-sm')
                              : (isGlobalDarkMode ? 'text-sidebar-foreground/80 lg:hover:text-sidebar-foreground lg:hover:bg-sidebar-accent' : 'text-gray-700 lg:hover:text-gray-900 hover:bg-sidebar-light-hover')
                          )}
                        >
                          <div className="relative flex-shrink-0">
                            <Avatar className="w-10 h-10">
                              <AvatarImage src={channel.avatar} alt={channel.name} />
                              <AvatarFallback className={cn('text-xs text-white', channel.avatarColor || 'bg-slate-500')}>
                                {channel.name.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className={cn(
                              'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 transition-colors',
                              isActive ? (isGlobalDarkMode ? 'border-nav-active' : 'border-sidebar-light-active shadow-sm') : (isGlobalDarkMode ? 'border-sidebar' : 'border-white'),
                              hasOtherOnline ? 'bg-green-500' : 'bg-gray-400'
                            )} />
                          </div>
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="truncate">{channel.name}</span>
                                {channel.isPrivate && (
                                  <Lock className={cn('w-3.5 h-3.5 flex-shrink-0 opacity-50', isActive && (isGlobalDarkMode ? 'text-white opacity-100' : 'text-gray-900 opacity-100'))} />
                                )}
                              </div>
                              {channel.unreadCount > 0 && (
                                <span className={cn(
                                  'text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 font-bold',
                                  isActive ? 'bg-white text-nav-active shadow-sm' : 'bg-nav-active text-white'
                                )}>
                                  {channel.unreadCount}
                                </span>
                              )}
                            </div>
                            {channel.lastMessage && (
                              <p className={cn(
                                "text-[11px] truncate opacity-60 mt-0.5 font-normal",
                                isActive ? (isGlobalDarkMode ? "text-white/80" : subTextColor) : subTextColor
                              )}>
                                {channel.lastMessage}
                              </p>
                            )}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* ── Direct Messages ── */}
          <Collapsible open={dmsOpen} onOpenChange={setDmsOpen}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <CollapsibleTrigger asChild>
                  <button className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}>
                    {dmsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </CollapsibleTrigger>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
                  Direct Messages
                </h3>
              </div>
              <button
                onClick={e => { e.stopPropagation(); setNewMessageOpen(true); }}
                className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}
                title="New Message"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <CollapsibleContent>
              <ul className="space-y-1">
                {directMessages.map(dm => {
                  const participant = getDmParticipant(dm);
                  if (!participant) return null;
                  const isActive = activeDmId === dm.id;
                  return (
                    <li key={dm.id}>
                      <button
                        onClick={() => { setActiveDm(dm.id); onChatSelect?.(); }}
                        className={cn(
                          'w-full px-3 py-3 lg:py-2.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 text-left active:scale-[0.98]',
                          isActive
                            ? (isGlobalDarkMode ? 'bg-nav-active text-white shadow-md font-bold' : 'bg-sidebar-light-active text-gray-900 font-bold shadow-sm')
                            : (isGlobalDarkMode ? 'text-sidebar-foreground/80 lg:hover:text-sidebar-foreground lg:hover:bg-sidebar-accent' : 'text-gray-700 lg:hover:text-gray-900 hover:bg-sidebar-light-hover')
                        )}
                      >
                        <div className="relative">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={participant.avatar} />
                            <AvatarFallback className={cn('text-xs transition-colors', isActive ? 'bg-white/20 text-white' : (isGlobalDarkMode ? 'bg-sidebar-accent text-sidebar-foreground/80' : 'bg-slate-200 text-slate-700'))}>
                              {participant.displayName.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            'absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 transition-colors',
                            isActive ? (isGlobalDarkMode ? 'border-nav-active' : 'border-sidebar-light-active shadow-sm') : (isGlobalDarkMode ? 'border-sidebar' : 'border-white'),
                            onlineUsers.includes(participant.id) ? 'bg-green-500' : 'bg-gray-400'
                          )} />
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate">{participant.displayName}</span>
                            {dm.unreadCount > 0 && (
                              <span className={cn(
                                'text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 font-bold',
                                isActive ? 'bg-white text-nav-active shadow-sm' : 'bg-nav-active text-white'
                              )}>
                                {dm.unreadCount}
                              </span>
                            )}
                          </div>
                          {dm.lastMessage && (
                            <p className={cn(
                              "text-[11px] truncate opacity-60 mt-0.5 font-normal",
                              isActive ? (isGlobalDarkMode ? "text-white/80" : subTextColor) : subTextColor
                            )}>
                              {dm.lastMessage}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </Collapsible>

        </nav>
      </div>

      <NewMessageDialog open={newMessageOpen} onOpenChange={setNewMessageOpen} isGlobalDarkMode={isGlobalDarkMode} />
    </div>
  );
};

// ─── Inner layout (must be inside TeamChatProvider) ──────────────────────────

interface PortalInboxLayoutProps {
  projects: Project[];
  isGlobalDarkMode: boolean;
}

const PortalInboxLayout: React.FC<PortalInboxLayoutProps> = ({ projects, isGlobalDarkMode }) => {
  const { activeThreadId, activeChannelId, activeDmId } = useTeamChat();
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const hasChatSelected = activeChannelId || activeDmId;

  if (isMobile) {
    if (hasChatSelected) {
      return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-background">
          {activeThreadId ? (
            <ThreadPanel isGlobalDarkMode={isGlobalDarkMode} isMobile />
          ) : (
            <ChatArea
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails(v => !v)}
              isGlobalDarkMode={isGlobalDarkMode}
              isMobile
              onOpenSidebar={() => {}}
            />
          )}
        </div>
      );
    }
    return (
      <div className="h-full flex flex-col bg-background">
        <PortalChatSidebar projects={projects} isGlobalDarkMode={isGlobalDarkMode} onChatSelect={() => {}} />
      </div>
    );
  }

  return (
    <div className="h-full flex">
      <div className="w-[320px] flex-shrink-0 border-r border-border">
        <PortalChatSidebar projects={projects} isGlobalDarkMode={isGlobalDarkMode} />
      </div>
      <div className="flex-1 min-w-0 flex">
        <ResizablePanelGroup direction="horizontal" className="h-full flex-1 min-w-0">
          <ResizablePanel defaultSize={activeThreadId ? 65 : 100} minSize={40}>
            <ChatArea
              showDetails={showDetails}
              onToggleDetails={() => setShowDetails(v => !v)}
              isGlobalDarkMode={isGlobalDarkMode}
              isMobile={false}
            />
          </ResizablePanel>
          {activeThreadId && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={35} minSize={25} maxSize={50}>
                <ThreadPanel isGlobalDarkMode={isGlobalDarkMode} />
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </div>
    </div>
  );
};

// ─── Public export ────────────────────────────────────────────────────────────

export const PortalInbox: React.FC<{ isGlobalDarkMode?: boolean }> = ({ isGlobalDarkMode: propDarkMode }) => {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const outletContext = useOutletContext<{ isGlobalDarkMode: boolean }>();
  const isGlobalDarkMode = propDarkMode ?? outletContext?.isGlobalDarkMode ?? false;

  const loadProjects = useCallback(async () => {
    if (!currentWorkspace || !user) return;
    try {
      const data = await projectService.getProjectsForClient(currentWorkspace.id, user.id);
      setProjects(data);
    } catch (err) {
      console.error('Error loading client projects for inbox:', err);
    }
  }, [currentWorkspace?.id, user?.id]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  return (
    <>
      <Helmet>
        <title>Inbox | Client Portal</title>
      </Helmet>
      <div className="h-full">
        <TeamChatProvider basePath="/inbox">
          <PortalInboxLayout projects={projects} isGlobalDarkMode={isGlobalDarkMode} />
        </TeamChatProvider>
      </div>
    </>
  );
};

export default PortalInbox;
